import styled, { css } from "styled-components";
import { DEFAULT_ANIMATION_SPEED } from "../../constants";
import { colorMixin, focusShadowMixin } from "../../helpers";
import { TColor } from "../../types";
import { getButtonSizeStyles } from "./helpers";
import type { BaseButtonVariant, ButtonSize } from "./types";

enum ButtonState {
    Hover = "&:hover:not(:disabled):not(:active)",
    Active = "&:active:not(:disabled)",
    Focus = "&:focus-visible",
    Disabled = "&:disabled, [disabled]"
}

const getPrimaryHoverGradient = (leftColor: TColor, rightColor: TColor) => css`
    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(
            100% 866.98% at 0% 50%,
            ${colorMixin(leftColor)} 0%,
            ${colorMixin(rightColor)} 100%
        );
        opacity: 0;
        pointer-events: none;
        transition: all ease-in-out ${DEFAULT_ANIMATION_SPEED}ms;
        z-index: -1;
    }
`;

const baseStyles = css`
    position: relative;
    overflow: hidden;
    z-index: 0;

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;

    box-sizing: border-box;
    transition: all ease-in-out ${DEFAULT_ANIMATION_SPEED}ms;
    border-radius: 4px;

    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        cursor: not-allowed;
    }

    &:active {
        transition: none;
    }
`;

const primaryStyles = css`
    background-color: ${colorMixin("surface-1200")};
    border: unset;
    color: ${colorMixin("primary-base")};
    ${getPrimaryHoverGradient("surface-1200", "surface-1000")}

    ${ButtonState.Hover} {
        &::before {
            opacity: 1;
        }
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("surface-900")};

        &::before {
            transition: none;
        }
    }

    ${ButtonState.Focus} {
        ${focusShadowMixin("surface-100", "surface-800")}
    }

    ${ButtonState.Disabled} {
        background-color: ${colorMixin("surface-200")};
    }
`;

const primaryDestructiveStyles = css`
    background-color: ${colorMixin("error-700")};
    border: unset;
    color: ${colorMixin("primary-base")};

    ${ButtonState.Hover} {
        background-color: ${colorMixin("error-600")};
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("error-800")};
    }

    ${ButtonState.Focus} {
        ${focusShadowMixin("error-100", "error-800")}
    }

    ${ButtonState.Disabled} {
        background-color: ${colorMixin("error-200")};
    }
`;

const secondaryStyles = css`
    background-color: ${colorMixin("surface-base")};
    border: 1px solid ${colorMixin("primary-200")};
    color: ${colorMixin("primary-1200")};

    ${ButtonState.Hover} {
        background-color: ${colorMixin("surface-50")};
        border-color: ${colorMixin("primary-100")};
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("surface-100")};
        border-color: ${colorMixin("primary-100")};
    }

    ${ButtonState.Focus} {
        border-color: ${colorMixin("primary-100")};
        ${focusShadowMixin("surface-100", "surface-800")}
    }

    ${ButtonState.Disabled} {
        border-color: ${colorMixin("primary-100")};
        color: ${colorMixin("primary-500")};
    }
`;

const secondaryDestructiveStyles = css`
    background-color: ${colorMixin("surface-base")};
    border: 1px solid ${colorMixin("error-600")};
    color: ${colorMixin("error-900")};

    ${ButtonState.Hover} {
        border-color: ${colorMixin("error-800")};
        color: ${colorMixin("error-800")};
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("error-50")};
        border-color: ${colorMixin("error-500")};
        color: ${colorMixin("error-800")};
    }

    ${ButtonState.Focus} {
        border-color: ${colorMixin("error-500")};
        color: ${colorMixin("error-800")};
        ${focusShadowMixin("error-100", "error-800")}
    }

    ${ButtonState.Disabled} {
        border-color: ${colorMixin("error-500")};
        color: ${colorMixin("error-400")};
    }
`;

const tertiaryStyles = css`
    background-color: transparent;
    border: unset;
    color: ${colorMixin("primary-1200")};
    text-decoration: underline;

    ${ButtonState.Hover} {
        background-color: ${colorMixin("surface-base", 0.2)};
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("surface-50")};
    }

    ${ButtonState.Focus} {
        background-color: ${colorMixin("surface-base")};
        ${focusShadowMixin("primary-100", "primary-800")}
    }

    ${ButtonState.Disabled} {
        color: ${colorMixin("primary-500")};
    }
`;

const tertiaryDestructiveStyles = css`
    background-color: transparent;
    border: unset;
    color: ${colorMixin("error-700")};
    text-decoration: underline;

    ${ButtonState.Hover} {
        background-color: ${colorMixin("surface-base", 0.2)};
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("surface-50")};
        color: ${colorMixin("error-800")};
    }

    ${ButtonState.Focus} {
        background-color: ${colorMixin("error-50")};
        color: ${colorMixin("error-800")};
        outline: none;
    }

    ${ButtonState.Disabled} {
        color: ${colorMixin("error-400")};
    }
`;

const tertiaryInverseStyles = css`
    background-color: transparent;
    border: unset;
    color: ${colorMixin("primary-base")};
    text-decoration: underline;

    ${ButtonState.Hover} {
        background-color: ${colorMixin("surface-base", 0.2)};
    }

    ${ButtonState.Active} {
        color: ${colorMixin("primary-1200")};
        background-color: ${colorMixin("surface-50")};
    }

    ${ButtonState.Focus} {
        color: ${colorMixin("primary-1200")};
        background-color: ${colorMixin("surface-50")};
        ${focusShadowMixin("surface-100", "surface-800")}
    }

    ${ButtonState.Disabled} {
        color: ${colorMixin("primary-400")};
    }
`;

const tertiaryInverseDestructiveStyles = css`
    background-color: transparent;
    border: unset;
    color: ${colorMixin("error-400")};
    text-decoration: underline;

    ${ButtonState.Hover} {
        background-color: ${colorMixin("surface-base", 0.2)};
        color: ${colorMixin("error-400")};
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("surface-50")};
        color: ${colorMixin("error-700")};
    }

    ${ButtonState.Focus} {
        background-color: ${colorMixin("surface-50")};
        color: ${colorMixin("error-400")};
        ${focusShadowMixin("surface-100", "surface-800")}
    }

    ${ButtonState.Disabled} {
        color: ${colorMixin("error-300")};
    }
`;

const externalStyles = css`
    background-color: ${colorMixin("surface-base")};
    border: 1px solid ${colorMixin("surface-base")};
    color: ${colorMixin("tertiary-700")};

    ${ButtonState.Hover} {
        background-color: ${colorMixin("surface-25")};
        border: 1px solid ${colorMixin("primary-100")};
    }

    ${ButtonState.Active} {
        background-color: ${colorMixin("surface-50")};
        border: 1px solid ${colorMixin("primary-100")};
    }

    ${ButtonState.Focus} {
        background-color: ${colorMixin("surface-25")};
        border: 1px solid ${colorMixin("surface-50")};
        ${focusShadowMixin("primary-100", "primary-800")}
    }

    ${ButtonState.Disabled} {
        background-color: ${colorMixin("surface-base")};
        border: 1px solid ${colorMixin("primary-100")};
        color: ${colorMixin("primary-500")};
    }
`;

const externalDestructiveStyles = css`
    background-color: ${colorMixin("surface-base")};
    border: 1px solid ${colorMixin("surface-base")};
    color: ${colorMixin("error-700")};

    ${ButtonState.Hover} {
        background-color: ${colorMixin("surface-25")};
        border: 1px solid ${colorMixin("primary-100")};
    }

    ${ButtonState.Active} {
        color: ${colorMixin("error-800")};
        background-color: ${colorMixin("surface-50")};
        border: 1px solid ${colorMixin("primary-100")};
    }

    ${ButtonState.Focus} {
        background-color: ${colorMixin("error-50")};
        color: ${colorMixin("error-800")};
        outline: none;
    }

    ${ButtonState.Disabled} {
        background-color: ${colorMixin("surface-base")};
        border: 1px solid ${colorMixin("primary-100")};
        color: ${colorMixin("error-400")};
    }
`;

export const Button = styled.button<{
    $destructive: boolean;
    $variant: BaseButtonVariant;
    $size: ButtonSize;
    $iconOnly: boolean;
}>`
    ${baseStyles};
    ${({ $size, $iconOnly }) => getButtonSizeStyles($size, $iconOnly)};

    ${({ $destructive, $variant }) => {
        switch ($variant) {
            case "secondary":
                return $destructive ? secondaryDestructiveStyles : secondaryStyles;
            case "tertiary":
                return $destructive ? tertiaryDestructiveStyles : tertiaryStyles;
            case "tertiary-inverse":
                return $destructive ? tertiaryInverseDestructiveStyles : tertiaryInverseStyles;
            case "external":
                return $destructive ? externalDestructiveStyles : externalStyles;
            case "primary":
            default:
                return $destructive ? primaryDestructiveStyles : primaryStyles;
        }
    }};
`;

export const IconWrapper = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;

    width: var(--button-icon-size);
    height: var(--button-icon-size);

    svg {
        width: var(--button-icon-size);
        height: var(--button-icon-size);
        transition: all ease-in-out ${DEFAULT_ANIMATION_SPEED}ms;
    }
`;

export const LoaderWrapper = styled.div<{ $destructive: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;

    position: absolute;
    width: 100%;
    height: 100%;

    border-radius: 4px;

    background-color: ${({ $destructive }) => colorMixin($destructive ? "error-800" : "primary-1200", 0.3)};

    svg {
        stroke: ${colorMixin("primary-base")};
    }
`;
